import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Keyboard,
  useWindowDimensions,
  type StyleProp,
  type View as RNView,
  type ViewStyle,
} from 'react-native';

import { SpotlightLayer } from './SpotlightLayer';
import { SpotlightOverlay, type SpotlightCardProps } from './SpotlightOverlay';
import { padRect, type SpotlightPadding, type SpotlightRect } from './spotlightGeometry';

import { View } from '@/components/ui/view';

/** The highlight's corner radius when a zone names none. */
const DEFAULT_RADIUS = 12;

const NO_PADDING: SpotlightPadding = { x: 0, y: 0 };

type ZoneEntry = {
  node: RNView | null;
  radius: number;
  padding: SpotlightPadding;
};

type CurrentStep = { tourKey: string; zone: number };

/** What a tour's card is handed for the step on screen. */
export type SpotlightStep = SpotlightCardProps & {
  tourKey: string;
  zone: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  next: () => void;
  prev: () => void;
  stop: () => void;
};

export type SpotlightController = {
  /**
   * Shows the tour's first registered step, or `zone` when given. With no zone
   * registered yet it waits, and shows the first zone that registers before
   * `stop`. Returns whether a step showed now.
   */
  start: (zone?: number) => boolean;
  next: () => void;
  prev: () => void;
  stop: () => void;
};

type SpotlightContextValue = {
  register: (tourKey: string, zone: number, entry: ZoneEntry | null) => void;
  /** A zone's own layout changed, so its highlight may have moved. */
  relayout: (tourKey: string, zone: number) => void;
  controller: (tourKey: string) => SpotlightController;
};

const SpotlightContext = createContext<SpotlightContextValue | null>(null);

const IDLE_CONTROLLER: SpotlightController = {
  start: () => false,
  next: () => {},
  prev: () => {},
  stop: () => {},
};

function sortedZones(zones: Map<number, ZoneEntry> | undefined): number[] {
  return zones ? [...zones.keys()].sort((first, second) => first - second) : [];
}

function measure(node: RNView): Promise<SpotlightRect | null> {
  return new Promise((resolve) => {
    node.measureInWindow((x, y, width, height) => {
      resolve(width > 0 || height > 0 ? { x, y, width, height } : null);
    });
  });
}

type SpotlightTourProviderProps = {
  renderCard: (step: SpotlightStep) => ReactNode;
  backdropColor: string;
  /** The card's distance from the window's sides. */
  cardGutter?: number;
  /**
   * A tap on the scrim, the iOS escape gesture or Android's back button. Omit
   * to ignore them, for a tour that has to be finished or skipped from its card.
   */
  onDismiss?: (step: SpotlightStep) => void;
  /** A step reached the screen, measured and drawn. Called once each time the step on screen changes. */
  onStepShown?: (step: { tourKey: string; zone: number }) => void;
  children: ReactNode;
};

/**
 * Runs the guided tours beneath it, one step at a time, and draws the step on
 * screen as a spotlight over the whole window, the native header included.
 *
 * A tour is a set of zones under one key, shown in zone order. A zone that is
 * not registered when its turn comes is skipped. Each move reads the zones as
 * they are then, and measures its target when it shows, so a step never runs
 * on props or a position captured earlier. A target is measured again when its
 * own layout changes, the window resizes or the keyboard opens or closes.
 *
 * One step shows at a time across every tour the provider hosts; starting a
 * tour takes any other off the screen. A tour started before any of its zones
 * has mounted shows as soon as the first one does.
 */
export function SpotlightTourProvider({
  renderCard,
  backdropColor,
  cardGutter = 16,
  onDismiss,
  onStepShown,
  children,
}: SpotlightTourProviderProps) {
  const zonesRef = useRef(new Map<string, Map<number, ZoneEntry>>());

  const pendingStartsRef = useRef(new Set<string>());

  const onStepShownRef = useRef(onStepShown);

  useEffect(() => {
    onStepShownRef.current = onStepShown;
  });

  const [current, setCurrent] = useState<CurrentStep | null>(null);

  const currentRef = useRef<CurrentStep | null>(null);

  const [hole, setHole] = useState<SpotlightRect | null>(null);

  const measureIdRef = useRef(0);

  const window = useWindowDimensions();

  const show = useCallback((step: CurrentStep | null) => {
    currentRef.current = step;

    setCurrent(step);

    if (!step) {
      measureIdRef.current += 1;

      setHole(null);
    }
  }, []);

  const remeasure = useCallback(() => {
    const step = currentRef.current;

    const entry = step ? zonesRef.current.get(step.tourKey)?.get(step.zone) : undefined;

    const measureId = ++measureIdRef.current;

    if (!entry?.node) {
      setHole(null);

      return;
    }

    const { node, padding } = entry;

    requestAnimationFrame(() => {
      measure(node)
        .then((rect) => {
          if (measureId !== measureIdRef.current) {
            return;
          }

          setHole((previous) => {
            const next = rect ? padRect(rect, padding) : null;

            return previous &&
              next &&
              previous.x === next.x &&
              previous.y === next.y &&
              previous.width === next.width &&
              previous.height === next.height
              ? previous
              : next;
          });
        })
        .catch(() => undefined);
    });
  }, []);

  useEffect(() => {
    if (current) {
      remeasure();
    }
  }, [current, remeasure, window.width, window.height]);

  const hasHole = hole !== null;

  useEffect(() => {
    if (current && hasHole) {
      onStepShownRef.current?.(current);
    }
  }, [current, hasHole]);

  useEffect(() => {
    if (!current) {
      return;
    }

    const shown = Keyboard.addListener('keyboardDidShow', remeasure);

    const hidden = Keyboard.addListener('keyboardDidHide', remeasure);

    return () => {
      shown?.remove?.();

      hidden?.remove?.();
    };
  }, [current, remeasure]);

  const [context] = useState<SpotlightContextValue>(() => {
    const move = (tourKey: string, direction: 1 | -1) => {
      const step = currentRef.current;

      if (!step || step.tourKey !== tourKey) {
        return;
      }

      const zones = sortedZones(zonesRef.current.get(tourKey));

      const target =
        direction === 1
          ? zones.find((zone) => zone > step.zone)
          : [...zones].reverse().find((zone) => zone < step.zone);

      if (target !== undefined) {
        show({ tourKey, zone: target });
      }
    };

    const controllers = new Map<string, SpotlightController>();

    return {
      register: (tourKey, zone, entry) => {
        let zones = zonesRef.current.get(tourKey);

        if (!zones) {
          zones = new Map();

          zonesRef.current.set(tourKey, zones);
        }

        if (entry) {
          zones.set(zone, entry);
        } else {
          zones.delete(zone);
        }

        if (entry && pendingStartsRef.current.has(tourKey)) {
          pendingStartsRef.current.delete(tourKey);

          show({ tourKey, zone });

          return;
        }

        const step = currentRef.current;

        if (step?.tourKey === tourKey && step.zone === zone) {
          remeasure();
        }
      },

      relayout: (tourKey, zone) => {
        const step = currentRef.current;

        if (step?.tourKey === tourKey && step.zone === zone) {
          remeasure();
        }
      },

      controller: (tourKey) => {
        let controller = controllers.get(tourKey);

        if (!controller) {
          controller = {
            start: (zone) => {
              const zones = sortedZones(zonesRef.current.get(tourKey));

              const first = zone !== undefined && zones.includes(zone) ? zone : zones[0];

              if (first === undefined) {
                pendingStartsRef.current.add(tourKey);

                return false;
              }

              pendingStartsRef.current.delete(tourKey);

              show({ tourKey, zone: first });

              return true;
            },
            next: () => move(tourKey, 1),
            prev: () => move(tourKey, -1),
            stop: () => {
              pendingStartsRef.current.delete(tourKey);

              if (currentRef.current?.tourKey === tourKey) {
                show(null);
              }
            },
          };

          controllers.set(tourKey, controller);
        }

        return controller;
      },
    };
  });

  const step = current ? buildStep(current, zonesRef.current, context) : null;

  return (
    <SpotlightContext.Provider value={context}>
      {children}

      {step && hole ? (
        <SpotlightLayer onRequestClose={onDismiss ? () => onDismiss(withCard(step)) : undefined}>
          <SpotlightOverlay
            hole={hole}
            radius={step.radius}
            backdropColor={backdropColor}
            cardGutter={cardGutter}
            stepKey={`${step.tourKey}:${step.zone}`}
            onDismiss={onDismiss ? () => onDismiss(withCard(step)) : undefined}
            renderCard={(card) => renderCard({ ...step, ...card })}
          />
        </SpotlightLayer>
      ) : null}
    </SpotlightContext.Provider>
  );
}

/** The step on screen as its card sees it, from the zones registered now. */
function buildStep(
  { tourKey, zone }: CurrentStep,
  registry: Map<string, Map<number, ZoneEntry>>,
  context: SpotlightContextValue
) {
  const zones = sortedZones(registry.get(tourKey));

  const controller = context.controller(tourKey);

  return {
    tourKey,
    zone,
    isFirstStep: zones[0] === zone,
    isLastStep: zones[zones.length - 1] === zone,
    next: controller.next,
    prev: controller.prev,
    stop: controller.stop,
    radius: registry.get(tourKey)?.get(zone)?.radius ?? DEFAULT_RADIUS,
  };
}

/** A dismissal happens away from the card, so it carries no card position. */
function withCard(step: ReturnType<typeof buildStep>): SpotlightStep {
  return { ...step, placement: 'below', focusRef: { current: null } };
}

/** The controls for one tour. Every function keeps one identity for the provider's life. */
export function useSpotlightTour(tourKey: string): SpotlightController {
  const context = useContext(SpotlightContext);

  return useMemo(() => context?.controller(tourKey) ?? IDLE_CONTROLLER, [context, tourKey]);
}

type SpotlightZoneProps = {
  tourKey: string;
  zone: number;
  /** Whether the zone is registered as a step. The wrapper renders either way. */
  active: boolean;
  /** The highlight's corner radius. */
  radius?: number;
  /** Room the highlight leaves around the target on each side, per axis. The target does not move. */
  padding?: SpotlightPadding;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
};

/**
 * Marks its children as one step of a tour.
 *
 * Always renders the same wrapper and toggles only whether it is registered,
 * since swapping a wrapper in when a tour starts would remount everything
 * beneath it and drop a half-typed field's focus. Its latest props are what a
 * step uses, whenever it shows.
 */
export function SpotlightZone({
  tourKey,
  zone,
  active,
  radius = DEFAULT_RADIUS,
  padding = NO_PADDING,
  style,
  children,
}: SpotlightZoneProps) {
  const context = useContext(SpotlightContext);

  const nodeRef = useRef<RNView>(null);

  const paddingX = padding.x;

  const paddingY = padding.y;

  useEffect(() => {
    if (!context || !active) {
      return;
    }

    context.register(tourKey, zone, {
      node: nodeRef.current,
      radius,
      padding: { x: paddingX, y: paddingY },
    });
  }, [context, active, tourKey, zone, radius, paddingX, paddingY]);

  useEffect(() => {
    if (!context || !active) {
      return;
    }

    return () => context.register(tourKey, zone, null);
  }, [context, active, tourKey, zone]);

  return (
    <View
      ref={nodeRef}
      collapsable={false}
      style={style}
      onLayout={() => {
        if (active) {
          context?.relayout(tourKey, zone);
        }
      }}>
      {children}
    </View>
  );
}
